import axios from 'lib/axios'
import React, { useState } from 'react'
import { TabPanel, Tabs } from './Tabs'
import { CustomDatesEditor } from './CustomDatesEditor'
import { HolidayListEditor } from './HolidayListEditor'
import { TimezoneEditor } from './TimezoneEditor'
import { WeeklyHoursEditor } from './WeeklyHoursEditor'

function Section({ children }) {
  return <div style={{ marginBottom: 40 }}>{children}</div>
}

const BLANK_ENTRY = { date: '', name: '', opens_at: '09:00', closes_at: '17:00', closed: false }

function withInitialClosed(h) {
  return { ...h, closed: !h.opens_at && !h.closes_at }
}

// At submit time: when a row is inactive, normalize its local state so
// closed=true and hours are null. Active rows are unchanged.
function normalizeForSubmit(h) {
  if (h.active) return h
  return { ...h, closed: true, opens_at: null, closes_at: null }
}

// A custom date is a full-day closure when both hours are null, so a checked
// Closed box drops whatever hours the row was holding.
function stripCustomUiFields(r) {
  const dropHours = !!r.closed
  return {
    date:      r.date,
    name:      r.name,
    opens_at:  dropHours ? null : r.opens_at || null,
    closes_at: dropHours ? null : r.closes_at || null,
  }
}

function stripUiFields(h) {
  const active = !!h.active
  const closed = !!h.closed
  const dropHours = !active || closed
  return {
    name:      h.name,
    active:    active,
    opens_at:  dropHours ? null : h.opens_at || null,
    closes_at: dropHours ? null : h.closes_at || null,
  }
}

function useBusinessHours({
  initialWeeklyHours,
  saveUrl,
  bulkSaveHolidaysPath,
  holidays,
  initialCustomRules,
  initialTimezone,
}) {
  const [weeklyHours, setWeeklyHours] = useState(() => initialWeeklyHours.map((e) => ({ ...e, closed: !e.opens_at })))
  const [allHolidays, setAllHolidays] = useState(() => (holidays ?? []).map(withInitialClosed))
  const [customRules, setCustomRules] = useState(() => (initialCustomRules ?? []).map(withInitialClosed))
  const [timezone, setTimezone] = useState(initialTimezone || 'America/New_York')
  const [errors, setErrors] = useState({})
  const [dirty, setDirty] = useState({})

  function setError(key, message) {
    setErrors((prev) => ({ ...prev, [key]: message ?? null }))
  }

  function markDirty(key) {
    setDirty((prev) => (prev[key] ? prev : { ...prev, [key]: true }))
  }

  function markClean(key) {
    setDirty((prev) => (prev[key] ? { ...prev, [key]: false } : prev))
  }

  function extractErrorMessage(e) {
    const errs = e?.response?.data?.errors
    if (Array.isArray(errs) && errs.length) return errs.join(', ')
    return e?.response?.data?.error || e?.message || 'Failed to save'
  }

  function createSaveHandler(key, getFn) {
    return async () => {
      setError(key, null)
      try {
        const result = await getFn()
        markClean(key)
        return result
      } catch (e) {
        setError(key, extractErrorMessage(e))
      }
    }
  }

  const saveTimezone = createSaveHandler('timezone', async () => {
    const res = await axios.patch(saveUrl, { organization: { time_zone: timezone } })
    return res.data
  })

  const saveWeeklyHours = createSaveHandler('weeklyHours', async () => {
    const businessHours = Object.fromEntries(
      weeklyHours.map(({ day_of_week, opens_at, closes_at, closed }) => [
        day_of_week,
        closed ? { closed: true } : { closed: false, opens_at, closes_at },
      ])
    )
    const res = await axios.patch(saveUrl, { business_hours: businessHours })
    return res.data
  })

  const saveHolidays = createSaveHandler('holidays', async () => {
    const normalized = allHolidays.map(normalizeForSubmit)
    setAllHolidays(normalized)
    const { data } = await axios.post(bulkSaveHolidaysPath, {
      operation: 'save_holidays',
      holidays:  normalized.map(stripUiFields),
    })
    return data
  })

  const saveCustomRules = createSaveHandler('customRules', async () => {
    const { data } = await axios.post(bulkSaveHolidaysPath, {
      operation:    'save_custom',
      custom_rules: customRules.map(stripCustomUiFields),
    })
    // Sync from custom_rules, never from `resolved` — the latter only covers the current year, so
    // rebuilding state from it drops other-year rows and the next save would delete them.
    if (data?.custom_rules) {
      setCustomRules(
        data.custom_rules.map(({ date, name, opens_at, closes_at }) =>
          withInitialClosed({ date, name, opens_at, closes_at })
        )
      )
    }
    return data
  })

  function handleTimezoneChange(value) {
    setTimezone(value)
    markDirty('timezone')
  }

  function handleWeeklyHoursChange(day, patch) {
    setWeeklyHours((prev) => prev.map((entry, i) => (i === day ? { ...entry, ...patch } : entry)))
    markDirty('weeklyHours')
  }

  function handleHolidayChange(name, patch) {
    setAllHolidays((prev) => prev.map((h) => (h.name === name ? { ...h, ...patch } : h)))
    markDirty('holidays')
  }

  function handleCustomChange(index, entry) {
    setCustomRules((prev) => prev.map((r, i) => (i === index ? entry : r)))
    markDirty('customRules')
  }

  function handleCustomAdd() {
    setCustomRules((prev) => [...prev, { ...BLANK_ENTRY }])
    markDirty('customRules')
  }

  function handleCustomRemove(index) {
    setCustomRules((prev) => prev.filter((_, i) => i !== index))
    markDirty('customRules')
  }

  return {
    weeklyHours,
    allHolidays,
    customRules,
    timezone,
    errors,
    dirty,
    handlers: {
      weeklyHours:  handleWeeklyHoursChange,
      holidays:     handleHolidayChange,
      customChange: handleCustomChange,
      customAdd:    handleCustomAdd,
      customRemove: handleCustomRemove,
      timezone:     handleTimezoneChange,
    },
    savers: {
      weeklyHours: saveWeeklyHours,
      holidays:    saveHolidays,
      customRules: saveCustomRules,
      timezone:    saveTimezone,
    },
  }
}

export default function BusinessHolidaysEditor(props) {
  const [activeTab, setActiveTab] = useState(0)
  const { weeklyHours, allHolidays, customRules, timezone, errors, dirty, handlers, savers } = useBusinessHours(props)

  // Split the flat list into default-on (federal) and opt-in (additional) for display
  const defaultOnHolidays = allHolidays.filter((h) => h.default_on)
  const optInHolidays     = allHolidays.filter((h) => !h.default_on)

  return (
    <Tabs activeIndex={activeTab} setActiveIndex={setActiveTab}>
      <TabPanel label="Weekly Hours">
        <Section>
          <WeeklyHoursEditor
            weeklyHours={weeklyHours}
            onChange={handlers.weeklyHours}
            onSave={savers.weeklyHours}
            error={errors.weeklyHours}
            dirty={!!dirty.weeklyHours}
          />
        </Section>
      </TabPanel>

      <TabPanel label="Holidays & Custom Rules">
        <Section>
          <HolidayListEditor
            title="Recurring Federal Holidays"
            subTitle="All recurring federal holidays are observed by default. Uncheck to exclude a holiday."
            holidays={defaultOnHolidays}
            onChange={handlers.holidays}
            onSave={savers.holidays}
            error={errors.holidays}
            dirty={!!dirty.holidays}
          />
        </Section>
        <Section>
          <HolidayListEditor
            title="Additional Holidays"
            subTitle="These holidays are off by default. Toggle on to observe them."
            holidays={optInHolidays}
            onChange={handlers.holidays}
            onSave={savers.holidays}
            error={errors.holidays}
            dirty={!!dirty.holidays}
          />
        </Section>
        <Section>
          <CustomDatesEditor
            customDates={customRules}
            onChange={handlers.customChange}
            onAdd={handlers.customAdd}
            onRemove={handlers.customRemove}
            onSave={savers.customRules}
            error={errors.customRules}
            dirty={!!dirty.customRules}
          />
        </Section>
      </TabPanel>

      <TabPanel label="Timezone">
        <Section>
          <TimezoneEditor
            timezone={timezone}
            options={props.timezoneOptions || []}
            onChange={handlers.timezone}
            onSave={savers.timezone}
            error={errors.timezone}
            dirty={!!dirty.timezone}
          />
        </Section>
      </TabPanel>
    </Tabs>
  )
}
